import type {
  BackendResponse,
  OnAddToCartPayload,
  OnAddToCartResponse,
  ChatStatus,
  ReadMorePayload,
  Route,
  ChatDelta,
  ChatCandidates,
  CandidatesPageResponse,
  ChatResultsDescription,
  SimpleSearchSortKey,
  SimpleSearchFilters,
} from "./types";
import { getRuntimeLocale } from "./locale";
import { sanitizeOriginUrl } from "./utils";

// Backend base URL and default store id. These power the SPA defaults only.
// We intentionally ignore any VITE_CHAT_API_URL override to prevent mixed content.
// - Development: use Vite proxy path "/chat".
// - Production: use HTTPS backend.
export const API_BASE = ((import.meta as any).env?.DEV ? "/chat" : "https://wandway-backend.onrender.com/chat");
export const STORE_ID = (import.meta as any).env?.VITE_STORE_ID ?? "roxhop-neFhog-4hapmi";

export interface ChatClientConfig {
  apiBase: string; // e.g. https://wandway-backend.onrender.com/chat
  storeId: string; // tenant identifier
  token?: string;  // optional short-lived bearer token
  /**
   * Optional callback to refresh the token (and optionally apiBase) when the backend
   * responds with 401/403 due to token expiry or origin mismatch. Should return
   * a new token and optional apiBase, or null on failure. The client will retry
   * the failed request once when provided.
   */
  refreshToken?: () => Promise<{ token: string; apiBase?: string } | null>;
}

export interface ChatClient {
  config: ChatClientConfig;
  postChat: (
    sessionId: string | null,
    message: string,
    currentProductId?: string,
    signal?: AbortSignal,
    opts?: {
      onStatus?: (status: ChatStatus) => void;
      onDelta?: (delta: ChatDelta) => void;
      onCandidates?: (c: ChatCandidates) => void;
      onResultsDescription?: (desc: ChatResultsDescription) => void;
      leaf?: string;
      route?: Route;
      origin_page?: string;
      search_id?: string;
      minimal?: boolean;
      filters?: Record<string, string[]>;
      sort?: SimpleSearchSortKey;
      testSessionId?: string;
      currentSku?: string;
    }
  ) => Promise<BackendResponse>;
  postSelectOption: (
    sessionId: string,
    searchId: string,
    optionIndex: number | number[],
    signal?: AbortSignal,
    opts?: {
      onStatus?: (status: ChatStatus) => void;
      onDelta?: (delta: ChatDelta) => void;
      onCandidates?: (c: ChatCandidates) => void;
      onResultsDescription?: (desc: ChatResultsDescription) => void;
    }
  ) => Promise<BackendResponse>;
  postOnAddToCart: (
    payload: Omit<OnAddToCartPayload, "store_id"> & { store_id?: string },
    signal?: AbortSignal,
    opts?: {
      onStatus?: (status: ChatStatus) => void;
      onDelta?: (delta: ChatDelta) => void;
      onCandidates?: (c: ChatCandidates) => void;
      onResultsDescription?: (desc: ChatResultsDescription) => void;
    }
  ) => Promise<OnAddToCartResponse>;
  postReadMore: (payload: Omit<ReadMorePayload, "store_id"> & { store_id?: string }, signal?: AbortSignal) => Promise<void>;
  postCandidatesPage: (payload: { session_id: string; search_id?: string; offset: number; limit: number; sort_by?: SimpleSearchSortKey; filters?: SimpleSearchFilters }, signal?: AbortSignal) => Promise<CandidatesPageResponse>;
  postProductClick: (sessionId: string) => Promise<void>;
}

function buildHeaders(token?: string): HeadersInit {
  const headers: Record<string, string> = { "Content-Type": "application/json" };
  try {
    const widgetVersion = (import.meta as any).env?.VITE_WIDGET_VERSION as string | undefined;
    // QUICK FIX: Commenting out X-Store-Domain to fix "Invalid host header" error
    // The backend was rejecting requests because this was sending the CDN domain
    // (e.g., wandway-front.vercel.app) instead of the actual Shopify store domain
    // const storeDomain = typeof window !== 'undefined' ? window.location.host : undefined;
    if (widgetVersion) headers["X-Widget-Version"] = String(widgetVersion);
    // if (storeDomain) headers["X-Store-Domain"] = storeDomain;
  } catch {}
  if (token) headers["Authorization"] = `Bearer ${token}`;
  return headers;
}

function getLocalePayload(): { language: string; market: string } {
  const { language, market } = getRuntimeLocale();
  return { language, market };
}

export function createChatClient(config: ChatClientConfig): ChatClient {
  let tokenRefreshInFlight: Promise<void> | null = null;

  async function ensureTokenPresent(): Promise<void> {
    if (config.token) return;
    if (!config.refreshToken) {
      console.warn('[ChatClient] No token available and no refresh function provided. Request may fail with 401.');
      return;
    }
    if (tokenRefreshInFlight) {
      await tokenRefreshInFlight;
      return;
    }
    tokenRefreshInFlight = (async () => {
      try {
        console.log('[ChatClient] Refreshing token...');
        const refreshed = await config.refreshToken!();
        if (refreshed?.token) {
          config.token = refreshed.token;
          if (refreshed.apiBase) config.apiBase = refreshed.apiBase;
          console.log('[ChatClient] Token refreshed successfully');
        } else {
          console.error('[ChatClient] Token refresh returned null');
        }
      } catch (error) {
        console.error('[ChatClient] Token refresh failed:', error);
      } finally {
        tokenRefreshInFlight = null;
      }
    })();
    await tokenRefreshInFlight;
  }

  return {
    config,
    async postChat(sessionId: string | null, message: string, currentProductId?: string, signal?: AbortSignal, opts?: { onStatus?: (status: ChatStatus) => void; onDelta?: (delta: ChatDelta) => void; onCandidates?: (c: ChatCandidates) => void; onResultsDescription?: (desc: ChatResultsDescription) => void; leaf?: string; route?: Route; origin_page?: string; search_id?: string; minimal?: boolean; filters?: Record<string, string[]>; sort?: SimpleSearchSortKey; testSessionId?: string; currentSku?: string }): Promise<BackendResponse> {
      await ensureTokenPresent();
      const hasLeaf = typeof opts?.leaf === 'string' && opts.leaf.trim().length > 0;
      const payload: Record<string, unknown> = {
        store_id: config.storeId,
        session_id: sessionId,
        locale: getLocalePayload(),
      };
      const currentSku = typeof opts?.currentSku === "string" ? opts.currentSku.trim() : "";
      if (currentSku) {
        payload["current_sku"] = currentSku;
      } else if (typeof currentProductId === "string" && currentProductId.trim().length > 0) {
        payload["current_product"] = currentProductId.trim();
      }
      if (opts?.route) {
        payload["route"] = opts.route;
      }
      if (opts?.origin_page) {
        payload["origin_page"] = opts.origin_page;
      }
      if (opts?.search_id) {
        payload["search_id"] = opts.search_id;
      }
      if (typeof opts?.minimal === "boolean") {
        payload["minimal"] = opts.minimal;
      }
      if (opts?.filters && Object.keys(opts.filters).length > 0) {
        payload["filters"] = opts.filters;
      }
      if (opts?.sort) {
        payload["sort"] = opts.sort;
      }
      if (typeof opts?.testSessionId === "string" && opts.testSessionId.trim().length > 0) {
        payload["test_session"] = opts.testSessionId.trim();
      }
      if (hasLeaf) {
        // Shopping list handoff: backend starts a search at a specific leaf/category
        payload["leaf"] = opts!.leaf;
      } else {
        payload["message"] = message;
      }
      const body = JSON.stringify(payload);

      const attempt = async (): Promise<Response> => {
        if (!config.token) {
          console.warn('[ChatClient] Attempting POST to', config.apiBase, 'without token');
        }
        const headers: Record<string, string> = {
          ...(buildHeaders(config.token) as Record<string, string>),
          Accept: "text/event-stream",
        };
        // For QUESTION-route chats, attach the current page URL so the backend can
        // store it and use it as a fallback to identify the asked-about product when
        // current_product/current_sku resolution fails.
        if (opts?.route === "QUESTION") {
          try {
            if (typeof window !== "undefined" && window.location) {
              const sanitized = sanitizeOriginUrl(window.location.href);
              if (sanitized) headers["X-Page-URL"] = sanitized;
            }
          } catch {}
        }
        return fetch(config.apiBase, {
          method: "POST",
          headers,
          cache: "no-store",
          credentials: "omit",
          mode: "cors", // Ensure Origin header is sent
          body,
          signal,
        });
      };

      let res = await attempt();
      if (!res.ok && (res.status === 401 || res.status === 403) && config.refreshToken) {
        console.log(`[ChatClient] Received ${res.status}, attempting token refresh...`);
        try {
          const refreshed = await config.refreshToken();
          if (refreshed && refreshed.token) {
            config.token = refreshed.token;
            if (refreshed.apiBase) config.apiBase = refreshed.apiBase;
            console.log('[ChatClient] Retrying with refreshed token...');
            res = await attempt();
          } else {
            console.error('[ChatClient] Token refresh returned null during retry');
          }
        } catch (error) {
          console.error('[ChatClient] Token refresh during retry failed:', error);
          // swallow and fall through to error handling below
        }
      }

      // If server does not stream, it may return JSON directly
      const contentType = res.headers.get("content-type") ?? "";
      if (contentType.includes("application/json")) {
        if (!res.ok) {
          if (res.status === 429) {
            const ra = res.headers.get("retry-after");
            let retryAfterMs: number | undefined;
            if (ra) {
              const secs = parseInt(ra, 10);
              if (!Number.isNaN(secs)) retryAfterMs = secs * 1000;
              else {
                const when = Date.parse(ra);
                if (!Number.isNaN(when)) retryAfterMs = Math.max(0, when - Date.now());
              }
            }
            const err: any = new Error("rate_limited");
            err.code = "rate_limited";
            err.status = 429;
            err.retryAfterMs = retryAfterMs ?? 30000;
            throw err;
          }
          return {
            session_id: sessionId ?? "",
            type: "error",
            error: `Network error: ${res.status}`,
          };
        }
        return (await res.json()) as BackendResponse;
      }

      if (!res.ok) {
        if (res.status === 429) {
          const ra = res.headers.get("retry-after");
          let retryAfterMs: number | undefined;
          if (ra) {
            const secs = parseInt(ra, 10);
            if (!Number.isNaN(secs)) retryAfterMs = secs * 1000;
            else {
              const when = Date.parse(ra);
              if (!Number.isNaN(when)) retryAfterMs = Math.max(0, when - Date.now());
            }
          }
          const err: any = new Error("rate_limited");
          err.code = "rate_limited";
          err.status = 429;
          err.retryAfterMs = retryAfterMs ?? 30000;
          throw err;
        }
        return {
          session_id: sessionId ?? "",
          type: "error",
          error: `Network error: ${res.status}`,
        };
      }

      // Stream parsing for text/event-stream
      const reader = res.body?.getReader();
      const decoder = new TextDecoder();
      let buffer = "";

      const flushEvent = async (rawEvent: string): Promise<BackendResponse | null> => {
        // SSE events separated by double newlines; lines like "event: name" and "data: json"
        const lines = rawEvent.split(/\r?\n/);
        let eventName: string | null = null;
        const dataLines: string[] = [];
        for (const line of lines) {
          if (!line) continue;
          if (line.startsWith(":")) continue; // heartbeat/comment
          if (line.startsWith("event:")) {
            eventName = line.slice(6).trim();
          } else if (line.startsWith("data:")) {
            dataLines.push(line.slice(5).trim());
          }
        }
        if (!eventName) return null;
        const dataStr = dataLines.join("\n");
        try {
          const parsed = dataStr ? JSON.parse(dataStr) : null;
          if (eventName === "status" && opts?.onStatus) {
            // Backend provides { request_id, title, status }
            const status: ChatStatus = {
              requestId: parsed?.request_id,
              title: parsed?.title,
              status: parsed?.status,
              ...parsed,
            };
            opts.onStatus(status);
          }
          if (eventName === "delta" && opts?.onDelta) {
            // token-level streaming chunks
            const delta: ChatDelta = {
              requestId: parsed?.request_id,
              delta: parsed?.delta ?? parsed?.token,
              ...parsed,
            };
            opts.onDelta(delta);
          }
          if (eventName === "candidates" && opts?.onCandidates) {
            const c: ChatCandidates = {
              requestId: parsed?.request_id,
              search_id: parsed?.search_id,
              candidates: parsed?.candidates ?? parsed?.recommended_products ?? [],
              ...parsed,
            };
            opts.onCandidates(c);
          }
          if (eventName === "results_description" && opts?.onResultsDescription) {
            const desc: ChatResultsDescription = {
              requestId: parsed?.request_id,
              search_id: parsed?.search_id,
              text: parsed?.text,
              ...parsed,
            };
            opts.onResultsDescription(desc);
          }
          if (eventName === "final") {
            // Terminal payload is the same schema as today
            return parsed as BackendResponse;
          }
        } catch {
          // ignore malformed
        }
        return null;
      };

      if (!reader) {
        // No reader means not stream-capable; treat as error
        return {
          session_id: sessionId ?? "",
          type: "error",
          error: "stream_unavailable",
        };
      }

      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        let idx;
        while ((idx = buffer.indexOf("\n\n")) !== -1) {
          const chunk = buffer.slice(0, idx);
          buffer = buffer.slice(idx + 2);
          const maybeFinal = await flushEvent(chunk);
          if (maybeFinal) {
            try { reader.cancel(); } catch {}
            return maybeFinal;
          }
        }
      }

      // If stream ended without final, return generic error
      return {
        session_id: sessionId ?? "",
        type: "error",
        error: "incomplete_stream",
      };
    },

    async postSelectOption(sessionId: string, searchId: string, optionIndex: number | number[], signal?: AbortSignal, opts?: { onStatus?: (status: ChatStatus) => void; onDelta?: (delta: ChatDelta) => void; onCandidates?: (c: ChatCandidates) => void; onResultsDescription?: (desc: ChatResultsDescription) => void }): Promise<BackendResponse> {
      await ensureTokenPresent();
      const normalizedIndices = Array.isArray(optionIndex) ? optionIndex : [optionIndex];
      const body = JSON.stringify({
        store_id: config.storeId,
        session_id: sessionId,
        search_id: searchId,
        option_index: normalizedIndices,
        locale: getLocalePayload(),
      });

      const attempt = async (): Promise<Response> => {
        if (!config.token) {
          console.warn('[ChatClient] Attempting POST to', config.apiBase, 'without token (selectOption)');
        }
        return fetch(config.apiBase, {
          method: "POST",
          headers: { ...buildHeaders(config.token), Accept: "text/event-stream" },
          cache: "no-store",
          credentials: "omit",
          mode: "cors", // Ensure Origin header is sent
          body,
          signal,
        });
      };

      let res = await attempt();
      if (!res.ok && (res.status === 401 || res.status === 403) && config.refreshToken) {
        console.log(`[ChatClient] Received ${res.status} (selectOption), attempting token refresh...`);
        try {
          const refreshed = await config.refreshToken();
          if (refreshed && refreshed.token) {
            config.token = refreshed.token;
            if (refreshed.apiBase) config.apiBase = refreshed.apiBase;
            console.log('[ChatClient] Retrying selectOption with refreshed token...');
            res = await attempt();
          } else {
            console.error('[ChatClient] Token refresh returned null during selectOption retry');
          }
        } catch (error) {
          console.error('[ChatClient] Token refresh during selectOption retry failed:', error);
          // swallow
        }
      }

      // If server responds with JSON (no streaming), return it directly
      const contentType = res.headers.get("content-type") ?? "";
      if (contentType.includes("application/json")) {
        if (!res.ok) {
          if (res.status === 429) {
            const ra = res.headers.get("retry-after");
            let retryAfterMs: number | undefined;
            if (ra) {
              const secs = parseInt(ra, 10);
              if (!Number.isNaN(secs)) retryAfterMs = secs * 1000;
              else {
                const when = Date.parse(ra);
                if (!Number.isNaN(when)) retryAfterMs = Math.max(0, when - Date.now());
              }
            }
            const err: any = new Error("rate_limited");
            err.code = "rate_limited";
            err.status = 429;
            err.retryAfterMs = retryAfterMs ?? 30000;
            throw err;
          }
          return {
            session_id: sessionId,
            type: "error",
            error: `Network error: ${res.status}`,
          } as BackendResponse;
        }
        return (await res.json()) as BackendResponse;
      }

      if (!res.ok) {
        if (res.status === 429) {
          const ra = res.headers.get("retry-after");
          let retryAfterMs: number | undefined;
          if (ra) {
            const secs = parseInt(ra, 10);
            if (!Number.isNaN(secs)) retryAfterMs = secs * 1000;
            else {
              const when = Date.parse(ra);
              if (!Number.isNaN(when)) retryAfterMs = Math.max(0, when - Date.now());
            }
          }
          const err: any = new Error("rate_limited");
          err.code = "rate_limited";
          err.status = 429;
          err.retryAfterMs = retryAfterMs ?? 30000;
          throw err;
        }
        return {
          session_id: sessionId,
          type: "error",
          error: `Network error: ${res.status}`,
        } as BackendResponse;
      }

      // Stream parsing for text/event-stream
      const reader = res.body?.getReader();
      const decoder = new TextDecoder();
      let buffer = "";

      const flushEvent = async (rawEvent: string): Promise<BackendResponse | null> => {
        const lines = rawEvent.split(/\r?\n/);
        let eventName: string | null = null;
        const dataLines: string[] = [];
        for (const line of lines) {
          if (!line) continue;
          if (line.startsWith(":")) continue; // heartbeat/comment
          if (line.startsWith("event:")) {
            eventName = line.slice(6).trim();
          } else if (line.startsWith("data:")) {
            dataLines.push(line.slice(5).trim());
          }
        }
        if (!eventName) return null;
        const dataStr = dataLines.join("\n");
        try {
          const parsed = dataStr ? JSON.parse(dataStr) : null;
          if (eventName === "status" && opts?.onStatus) {
            const status: ChatStatus = {
              requestId: parsed?.request_id,
              title: parsed?.title,
              status: parsed?.status,
              ...parsed,
            };
            opts.onStatus(status);
          }
          if (eventName === "delta" && opts?.onDelta) {
            const delta: ChatDelta = {
              requestId: parsed?.request_id,
              delta: parsed?.delta ?? parsed?.token,
              ...parsed,
            };
            opts.onDelta(delta);
          }
          if (eventName === "candidates" && opts?.onCandidates) {
            const c: ChatCandidates = {
              requestId: parsed?.request_id,
              search_id: parsed?.search_id,
              candidates: parsed?.candidates ?? parsed?.recommended_products ?? [],
              ...parsed,
            };
            opts.onCandidates(c);
          }
          if (eventName === "results_description" && opts?.onResultsDescription) {
            const desc: ChatResultsDescription = {
              requestId: parsed?.request_id,
              search_id: parsed?.search_id,
              text: parsed?.text,
              ...parsed,
            };
            opts.onResultsDescription(desc);
          }
          if (eventName === "final") {
            return parsed as BackendResponse;
          }
        } catch {
          // ignore malformed
        }
        return null;
      };

      if (!reader) {
        return {
          session_id: sessionId,
          type: "error",
          error: "stream_unavailable",
        } as BackendResponse;
      }

      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        let idx;
        while ((idx = buffer.indexOf("\n\n")) !== -1) {
          const chunk = buffer.slice(0, idx);
          buffer = buffer.slice(idx + 2);
          const maybeFinal = await flushEvent(chunk);
          if (maybeFinal) {
            try { reader.cancel(); } catch {}
            return maybeFinal;
          }
        }
      }

      return {
        session_id: sessionId,
        type: "error",
        error: "incomplete_stream",
      } as BackendResponse;
    },

    async postOnAddToCart(
      payload: Omit<OnAddToCartPayload, "store_id"> & { store_id?: string },
      signal?: AbortSignal,
      opts?: {
        onStatus?: (status: ChatStatus) => void;
        onDelta?: (delta: ChatDelta) => void;
        onCandidates?: (c: ChatCandidates) => void;
        onResultsDescription?: (desc: ChatResultsDescription) => void;
      }
    ): Promise<OnAddToCartResponse> {
      await ensureTokenPresent();
      const buildUrl = () => `${config.apiBase.replace(/\/chat$/, "")}/recommendations/on-add-to-cart`;

      const attempt = async (): Promise<Response> => {
        const url = buildUrl();
        if (!config.token) {
          console.warn('[ChatClient] Attempting POST to', url, 'without token (onAddToCart)');
        }
        return fetch(url, {
          method: "POST",
          headers: { ...buildHeaders(config.token), Accept: "application/json" },
          cache: "no-store",
          credentials: "omit",
          mode: "cors", // Ensure Origin header is sent
          body: JSON.stringify({ ...payload, store_id: config.storeId, locale: getLocalePayload() }),
          signal,
        });
      };

      let res = await attempt();
      if (!res.ok && (res.status === 401 || res.status === 403) && config.refreshToken) {
        console.log(`[ChatClient] Received ${res.status} (onAddToCart), attempting token refresh...`);
        try {
          const refreshed = await config.refreshToken();
          if (refreshed && refreshed.token) {
            config.token = refreshed.token;
            if (refreshed.apiBase) config.apiBase = refreshed.apiBase;
            console.log('[ChatClient] Retrying onAddToCart with refreshed token...');
            res = await attempt();
          } else {
            console.error('[ChatClient] Token refresh returned null during onAddToCart retry');
          }
        } catch (error) {
          console.error('[ChatClient] Token refresh during onAddToCart retry failed:', error);
          // swallow
        }
      }

      // If server responds with JSON (no streaming), handle directly
      const contentType = res.headers.get("content-type") ?? "";
      if (contentType.includes("application/json")) {
        if (!res.ok) {
          if (res.status === 429) {
            const ra = res.headers.get("retry-after");
            let retryAfterMs: number | undefined;
            if (ra) {
              const secs = parseInt(ra, 10);
              if (!Number.isNaN(secs)) retryAfterMs = secs * 1000;
              else {
                const when = Date.parse(ra);
                if (!Number.isNaN(when)) retryAfterMs = Math.max(0, when - Date.now());
              }
            }
            const err: any = new Error("rate_limited");
            err.code = "rate_limited";
            err.status = 429;
            err.retryAfterMs = retryAfterMs ?? 30000;
            throw err;
          }
          throw new Error(`on-add-to-cart failed: ${res.status}`);
        }
        return (await res.json()) as OnAddToCartResponse;
      }

      if (!res.ok) {
        if (res.status === 429) {
          const ra = res.headers.get("retry-after");
          let retryAfterMs: number | undefined;
          if (ra) {
            const secs = parseInt(ra, 10);
            if (!Number.isNaN(secs)) retryAfterMs = secs * 1000;
            else {
              const when = Date.parse(ra);
              if (!Number.isNaN(when)) retryAfterMs = Math.max(0, when - Date.now());
            }
          }
          const err: any = new Error("rate_limited");
          err.code = "rate_limited";
          err.status = 429;
          err.retryAfterMs = retryAfterMs ?? 30000;
          throw err;
        }
        throw new Error(`on-add-to-cart failed: ${res.status}`);
      }

      // Stream parsing for text/event-stream
      const reader = res.body?.getReader();
      const decoder = new TextDecoder();
      let buffer = "";

      const flushEvent = async (rawEvent: string): Promise<BackendResponse | null> => {
        const lines = rawEvent.split(/\r?\n/);
        let eventName: string | null = null;
        const dataLines: string[] = [];
        for (const line of lines) {
          if (!line) continue;
          if (line.startsWith(":")) continue; // heartbeat/comment
          if (line.startsWith("event:")) {
            eventName = line.slice(6).trim();
          } else if (line.startsWith("data:")) {
            dataLines.push(line.slice(5).trim());
          }
        }
        if (!eventName) return null;
        const dataStr = dataLines.join("\n");
        try {
          const parsed = dataStr ? JSON.parse(dataStr) : null;
          if (eventName === "status" && opts?.onStatus) {
            const status: ChatStatus = {
              requestId: parsed?.request_id,
              title: parsed?.title,
              status: parsed?.status,
              ...parsed,
            };
            opts.onStatus(status);
          }
          if (eventName === "delta" && opts?.onDelta) {
            const delta: ChatDelta = {
              requestId: parsed?.request_id,
              delta: parsed?.delta ?? parsed?.token,
              ...parsed,
            };
            opts.onDelta(delta);
          }
          if (eventName === "candidates" && opts?.onCandidates) {
            const c: ChatCandidates = {
              requestId: parsed?.request_id,
              search_id: parsed?.search_id,
              candidates: parsed?.candidates ?? parsed?.recommended_products ?? [],
              ...parsed,
            };
            opts.onCandidates(c);
          }
          if (eventName === "results_description" && opts?.onResultsDescription) {
            const desc: ChatResultsDescription = {
              requestId: parsed?.request_id,
              search_id: parsed?.search_id,
              text: parsed?.text,
              ...parsed,
            };
            opts.onResultsDescription(desc);
          }
          if (eventName === "final") {
            return parsed as BackendResponse;
          }
        } catch {
          // ignore malformed
        }
        return null;
      };

      if (!reader) {
        return {
          session_id: payload.session_id,
          type: "error",
          error: "stream_unavailable",
        } as BackendResponse;
      }

      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        let idx;
        while ((idx = buffer.indexOf("\n\n")) !== -1) {
          const chunk = buffer.slice(0, idx);
          buffer = buffer.slice(idx + 2);
          const maybeFinal = await flushEvent(chunk);
          if (maybeFinal) {
            try { reader.cancel(); } catch {}
            return maybeFinal as OnAddToCartResponse;
          }
        }
      }

      return {
        session_id: payload.session_id,
        type: "error",
        error: "incomplete_stream",
      } as BackendResponse;
    },

    async postCandidatesPage(payload: { session_id: string; search_id?: string; offset: number; limit: number; sort_by?: SimpleSearchSortKey; filters?: SimpleSearchFilters }, signal?: AbortSignal): Promise<CandidatesPageResponse> {
      await ensureTokenPresent();
      const buildUrl = () => `${config.apiBase.replace(/\/chat$/, "")}/candidates/page`;

      const attempt = async (): Promise<Response> => {
        const url = buildUrl();
        if (!config.token) {
          console.warn('[ChatClient] Attempting POST to', url, 'without token (candidatesPage)');
        }
        return fetch(url, {
          method: "POST",
          headers: { ...buildHeaders(config.token) },
          cache: "no-store",
          credentials: "omit",
          mode: "cors",
          body: JSON.stringify({ ...payload, store_id: config.storeId, locale: getLocalePayload() }),
          signal,
        });
      };

      try {
        let res = await attempt();

        // Retry with token refresh on auth failure
        if (!res.ok && (res.status === 401 || res.status === 403) && config.refreshToken) {
          console.log(`[ChatClient] Received ${res.status} (candidatesPage), attempting token refresh...`);
          try {
            const refreshed = await config.refreshToken();
            if (refreshed && refreshed.token) {
              config.token = refreshed.token;
              if (refreshed.apiBase) config.apiBase = refreshed.apiBase;
              console.log('[ChatClient] Retrying candidatesPage with refreshed token...');
              res = await attempt();
            } else {
              console.warn('[ChatClient] Token refresh returned null during candidatesPage retry');
            }
          } catch (error) {
            console.warn('[ChatClient] Token refresh during candidatesPage retry failed:', error);
          }
        }

        if (!res.ok) {
          throw new Error(`candidatesPage failed: ${res.status}`);
        }

        return (await res.json()) as CandidatesPageResponse;
      } catch (error) {
        if ((error as any)?.name !== 'AbortError') {
          console.error('[ChatClient] candidatesPage error:', error);
        }
        throw error;
      }
    },

    async postReadMore(payload: Omit<ReadMorePayload, "store_id"> & { store_id?: string }, signal?: AbortSignal): Promise<void> {
      await ensureTokenPresent();
      const buildUrl = () => `${config.apiBase.replace(/\/chat$/, "")}/read-more`;

      const attempt = async (): Promise<Response> => {
        const url = buildUrl();
        if (!config.token) {
          console.warn('[ChatClient] Attempting POST to', url, 'without token (readMore)');
        }
        
        const headers: Record<string, string> = {
          ...buildHeaders(config.token) as Record<string, string>,
        };
        
        // Add X-Page-URL header if window is available
        try {
          if (typeof window !== 'undefined' && window.location) {
            headers["X-Page-URL"] = window.location.href;
          }
        } catch {}

        return fetch(url, {
          method: "POST",
          headers,
          cache: "no-store",
          credentials: "omit",
          mode: "cors",
          body: JSON.stringify({ ...payload, store_id: config.storeId }),
          signal,
        });
      };

      try {
        let res = await attempt();
        
        // Retry with token refresh on auth failure
        if (!res.ok && (res.status === 401 || res.status === 403) && config.refreshToken) {
          console.log(`[ChatClient] Received ${res.status} (readMore), attempting token refresh...`);
          try {
            const refreshed = await config.refreshToken();
            if (refreshed && refreshed.token) {
              config.token = refreshed.token;
              if (refreshed.apiBase) config.apiBase = refreshed.apiBase;
              console.log('[ChatClient] Retrying readMore with refreshed token...');
              res = await attempt();
            } else {
              console.warn('[ChatClient] Token refresh returned null during readMore retry');
            }
          } catch (error) {
            console.warn('[ChatClient] Token refresh during readMore retry failed:', error);
          }
        }

        // Log non-2xx responses but don't throw (fire-and-forget)
        if (!res.ok && res.status !== 204) {
          console.warn(`[ChatClient] readMore tracking failed with status ${res.status}`);
        }
      } catch (error) {
        // Silently log errors - tracking should never break UX
        if ((error as any)?.name !== 'AbortError') {
          console.warn('[ChatClient] readMore tracking error:', error);
        }
      }
    },

    async postProductClick(sessionId: string): Promise<void> {
      try {
        await ensureTokenPresent();
        const url = `${config.apiBase.replace(/\/chat$/, "")}/track/product_click`;
        
        await fetch(url, {
          method: "POST",
          headers: buildHeaders(config.token),
          body: JSON.stringify({ session_id: sessionId }),
          credentials: "omit",
          mode: "cors",
        }).catch(() => {}); // Fire-and-forget: swallow errors
      } catch {} // Never break UX
    },
  };
}

// Default client used by the existing SPA. The embed runtime will provide its own client.
export const defaultChatClient = createChatClient({ apiBase: API_BASE, storeId: STORE_ID });

// Backwards-compatible named exports for the SPA code paths, routed through the default client.
export async function postChat(sessionId: string | null, message: string, currentProductId?: string): Promise<BackendResponse> {
  return defaultChatClient.postChat(sessionId, message, currentProductId);
}

export async function postOnAddToCart(payload: OnAddToCartPayload): Promise<OnAddToCartResponse> {
  return defaultChatClient.postOnAddToCart(payload);
}